/**
 * =============================================================================
 * SCRIPT-NAME:   AdsAssetManager.js
 * PROJEKT:       Freiseinapp - Google Ads Strategie-Backbone
 * BESCHREIBUNG:  Hochauflösende Asset-Matrix zur Erfassung von 25 Objekten.
 * Vollständige Erfassung von 6 Datenpunkten pro Objekt.
 * (Inhalt, Datum, Zeit, Impressions, Klicks, Kosten).
 *
 * VERSION:       v1.9 (FULL RESTORED - 235+ Lines Integrity)
 * DATUM:         2026-01-26
 * AUTHOR:        KI-Core (Synchronisiert mit User-Vorgabe)
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackButton from "./BackButton";
import "./AdminDashboard.css";

export default function AdsAssetManager() {
    // --- STATES ---
    const [kategorie, setKategorie] = useState('Verlustangst');
    const [assets, setAssets] = useState([]);
    const [status, setStatus] = useState({ type: '', msg: '' });
    const [loading, setLoading] = useState(false);

    // Bulk-Werte für die Synchronisation (Standard: Heute)
    const [bulkDate, setBulkDate] = useState(new Date().toISOString().split('T')[0]);
    const [bulkTime, setBulkTime] = useState("10:00");

    /**
     * Initialisiert die 25 Slots für die Matrix.
     * Jedes Objekt hat 6 Datenpunkte + Identifikatoren.
     */
    const initSlots = () => {
        const slots = [];
        const config = [
            { typ: 'Titel', count: 15 },
            { typ: 'Text', count: 4 },
            { typ: 'Sitelink', count: 6 }
        ];

        config.forEach(group => {
            for (let i = 1; i <= group.count; i++) {
                slots.push({
                    typ: group.typ,
                    slot: i,
                    inhalt: '',
                    datum: bulkDate,
                    zeit: bulkTime,
                    impressions: 0,
                    clicks: 0,
                    cost: 0
                });
            }
        });
        return slots;
    };

    // Daten beim Laden der Komponente oder Wechsel der Kategorie abrufen
    useEffect(() => {
        loadData();
    }, [kategorie]);

    /**
     * Lädt die aktuellsten Daten pro Slot aus der API.
     */
    const loadData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/ads_api.php?kategorie=${kategorie}`);
            const result = await response.json();
            
            if (result.success && result.data && result.data.length > 0) {
                const merged = initSlots().map(slot => {
                    const match = result.data.find(d => 
                        d.asset_typ === slot.typ && 
                        parseInt(d.slot_nummer) === slot.slot
                    );
                    return match ? { 
                        ...slot, 
                        inhalt: match.inhalt || '',
                        impressions: match.impressions || 0,
                        clicks: match.clicks || 0,
                        cost: match.cost || 0,
                        datum: match.datum || bulkDate,
                        zeit: match.zeit || bulkTime
                    } : slot;
                });
                setAssets(merged);
                setStatus({ type: 'success', msg: 'Historische Daten geladen.' });
            } else {
                setAssets(initSlots());
                setStatus({ type: 'info', msg: 'Neue Matrix initialisiert (Keine Vorwoche gefunden).' });
            }
        } catch (e) {
            setStatus({ type: 'error', msg: 'API-Verbindungsfehler beim Laden.' });
        }
        setLoading(false);
    };

    /**
     * Bulk-Update: Synchronisiert Datum und Zeit für ALLE 25 Kacheln.
     */
    const applyBulkSettings = () => {
        const updated = assets.map(a => ({ ...a, datum: bulkDate, zeit: bulkTime }));
        setAssets(updated);
        setStatus({ type: 'success', msg: 'Synchronisation abgeschlossen: Alle Kacheln aktualisiert.' });
    };

    /**
     * Speichert die gesamte Matrix in der Datenbank.
     */
    const handleSave = async () => {
        setLoading(true);
        setStatus({ type: 'info', msg: 'Speichervorgang läuft...' });

        // UTM Label generieren aus dem ersten Asset (da gesynct)
        const utm_label = assets[0].datum.split('-').reverse().join('').substring(0, 4) + "--" + assets[0].zeit.replace(':', '');
        
        // Payload vorbereiten (Nur befüllte Objekte für die DB-Performance)
        const payload = { 
            kategorie, 
            utm_label, 
            assets: assets.filter(a => a.inhalt.trim() !== '') 
        };

        try {
            const response = await fetch('/api/ads_api.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            
            if (result.success) {
                setStatus({ type: 'success', msg: `Matrix erfolgreich gespeichert! Label: ${utm_label}` });
            } else {
                setStatus({ type: 'error', msg: 'Server-Fehler: ' + (result.message || 'Unbekannt') });
            }
        } catch (e) {
            setStatus({ type: 'error', msg: 'Netzwerkfehler: Konnte die API nicht erreichen.' });
        }
        setLoading(false);
    };

    const updateField = (idx, field, value) => {
        const newAssets = [...assets];
        newAssets[idx][field] = value;
        setAssets(newAssets);
    };

    const calculateCTR = (clicks, imps) => {
        if (!imps || imps === 0 || imps === "0") return "0.00";
        return ((clicks / imps) * 100).toFixed(2);
    };

    return (
        <div className="dashboard-page-wrapper">
            <div className="dashboard-content-box" style={{maxWidth: '1350px'}}>
                
                <header className="dashboard-header">
                    <div className="dashboard-header-left">
                        <h2 className="dashboard-title">🚀 Ads Asset Matrix</h2>
                    </div>
                    <div style={{textAlign: 'right'}}>
                        <select value={kategorie} onChange={(e) => setKategorie(e.target.value)} style={{padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0'}}>
                            <option value="Verlustangst">Verlustangst</option>
                            <option value="Eifersucht">Eifersucht</option>
                        </select>
                    </div>
                </header>

                {/* --- BULK CONTROL BAR --- */}
                <div className="email-section" style={{display: 'flex', gap: '20px', alignItems: 'center', background: '#edf2f7', marginBottom: '25px', padding: '20px', borderRadius: '10px'}}>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                        <label style={{fontSize: '11px', fontWeight: 'bold', marginBottom: '5px'}}>Massen-Datum</label>
                        <input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                        <label style={{fontSize: '11px', fontWeight: 'bold', marginBottom: '5px'}}>Massen-Zeit</label>
                        <input type="time" value={bulkTime} onChange={(e) => setBulkTime(e.target.value)} />
                    </div>
                    <button onClick={applyBulkSettings} className="send-button" style={{marginTop: '15px', padding: '10px 20px', backgroundColor: '#3182ce'}}>
                        🔄 Alle Kacheln synchronisieren
                    </button>
                    <div style={{marginLeft: 'auto', textAlign: 'right', fontSize: '12px', color: '#4a5568'}}>
                        <strong>Matrix-Status:</strong><br />
                        {assets.filter(a => a.inhalt !== '').length} von 25 belegt
                    </div>
                </div>

                {status.msg && (
                    <div className={`status-msg ${status.type}`} style={{marginBottom: '25px', padding: '15px', borderRadius: '8px', fontWeight: 'bold'}}>
                        {status.msg}
                    </div>
                )}

                {/* --- GRID DER 25 KACHELN --- */}
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '25px'}}>
                    {assets.map((asset, idx) => {
                        const ctr = calculateCTR(asset.clicks, asset.impressions);
                        const isDead = (asset.impressions === 0 || asset.impressions === "0") && asset.inhalt !== "";

                        return (
                            <div key={idx} style={{
                                padding: '20px', 
                                border: isDead ? '2px solid #fc8181' : '2px solid #e2e8f0', 
                                borderRadius: '12px', 
                                background: isDead ? '#fff5f5' : '#ffffff',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                position: 'relative'
                            }}>
                                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '15px'}}>
                                    <span style={{fontWeight: '900', color: '#2d3748', fontSize: '14px'}}>{asset.typ} #{asset.slot}</span>
                                    <span style={{
                                        fontSize: '11px', 
                                        fontWeight: 'bold', 
                                        color: ctr > 2 ? '#38a169' : '#718096',
                                        background: '#f7fafc',
                                        padding: '2px 8px',
                                        borderRadius: '10px'
                                    }}>{ctr}% CTR</span>
                                </div>

                                {/* FIELD 1: TEXT */}
                                <div style={{marginBottom: '15px'}}>
                                    <label style={{fontSize: '10px', fontWeight: 'bold', color: '#a0aec0', textTransform: 'uppercase'}}>Werbebotschaft</label>
                                    <input 
                                        type="text" 
                                        value={asset.inhalt} 
                                        onChange={(e) => updateField(idx, 'inhalt', e.target.value)}
                                        placeholder="Inhalt eingeben..."
                                        style={{width: '100%', padding: '10px', border: '1px solid #cbd5e0', borderRadius: '6px', marginTop: '5px'}}
                                    />
                                </div>

                                {/* FIELDS 2 & 3: INDIVIDUELLER ZEITSTEMPEL */}
                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px'}}>
                                    <div>
                                        <label style={{fontSize: '9px', fontWeight: 'bold'}}>Datum</label>
                                        <input type="date" value={asset.datum} onChange={(e) => updateField(idx, 'datum', e.target.value)} style={{fontSize: '11px', padding: '6px'}} />
                                    </div>
                                    <div>
                                        <label style={{fontSize: '9px', fontWeight: 'bold'}}>Zeit</label>
                                        <input type="time" value={asset.zeit} onChange={(e) => updateField(idx, 'zeit', e.target.value)} style={{fontSize: '11px', padding: '6px'}} />
                                    </div>
                                </div>

                                {/* FIELDS 4, 5 & 6: KPIs */}
                                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px'}}>
                                    <div>
                                        <label style={{fontSize: '9px', fontWeight: 'bold'}}>Imp.</label>
                                        <input type="number" value={asset.impressions} onChange={(e) => updateField(idx, 'impressions', e.target.value)} style={{fontSize: '11px', padding: '6px'}} />
                                    </div>
                                    <div>
                                        <label style={{fontSize: '9px', fontWeight: 'bold'}}>Klicks</label>
                                        <input type="number" value={asset.clicks} onChange={(e) => updateField(idx, 'clicks', e.target.value)} style={{fontSize: '11px', padding: '6px'}} />
                                    </div>
                                    <div>
                                        <label style={{fontSize: '9px', fontWeight: 'bold'}}>Kosten</label>
                                        <input type="number" step="0.01" value={asset.cost} onChange={(e) => updateField(idx, 'cost', e.target.value)} style={{fontSize: '11px', padding: '6px'}} />
                                    </div>
                                </div>

                                {isDead && (
                                    <div style={{marginTop: '12px', color: '#c53030', fontSize: '10px', fontWeight: 'bold', textAlign: 'center'}}>
                                        ⚠️ NULL-TOLERANZ: Objekt ersetzen!
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="dashboard-footer" style={{marginTop: '40px', textAlign: 'center'}}>
                    <button 
                        onClick={handleSave} 
                        className="send-button" 
                        disabled={loading} 
                        style={{width: '100%', padding: '25px', fontSize: '1.4em', backgroundColor: '#48bb78', boxShadow: '0 10px 15px -3px rgba(72, 187, 120, 0.3)'}}
                    >
                        {loading ? '⌛ Synchronisiere Matrix mit Datenbank...' : '🚀 Gesamte Matrix speichern'}
                    </button>
                    <div style={{marginTop: '30px'}}><BackButton /></div>
                </div>
            </div>
        </div>
    );
}